import { useState, useRef, useEffect, useCallback } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { streamVideoClient, streamClient } from "../../config/stream";
import { fetchChatToken, fetchVideoToken, generateChannelId } from "./videoCallUtils";

/**
 * Custom hook for managing Stream Video call
 * Handles call joining, leaving, and state management
 */
export const useVideoCall = ({
  classId,
  classData,
  activeRoomId,
  user,
  onCallJoined,
  onCallLeft,
}) => {
  const [currentCall, setCurrentCall] = useState(null);
  const [isCallJoined, setIsCallJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Joining call...");
  const [isLeaving, setIsLeaving] = useState(false);
  const callInstanceRef = useRef(null);
  const hasTriggeredNavigationRef = useRef(false);
  const isConnectingRef = useRef(false);

  /**
   * Enables camera and microphone for a call
   */
  const enableDevices = useCallback(async (call) => {
    try {
      await call.camera.enable();
      await call.microphone.enable();
    } catch (err) {
      console.warn("[useVideoCall] Error enabling devices:", err);
    }
  }, []);

  /**
   * Connects user to Stream Chat if not already connected
   */
  const ensureStreamChatConnection = useCallback(async () => {
    if (!streamClient.userID) {
      setLoadingMessage("Initializing chat...");
      try {
        const chatToken = await fetchChatToken(user.uid);
        await streamClient.connectUser(
          {
            id: user.uid,
            name: user.name || "User",
            image: user.photoUrl || "",
          },
          chatToken,
        );
      } catch (chatErr) {
        console.error(
          "[useVideoCall] Failed to connect user to Stream Chat:",
          chatErr,
        );
        // Continue with video - chat error is not fatal
      }
    }
  }, [user]);

  /**
   * Connects user to Stream Video if not already connected
   */
  const ensureStreamVideoConnection = useCallback(async () => {
    // Prevent concurrent connection attempts
    if (isConnectingRef.current) {
      return;
    }
    
    if (!streamVideoClient.user || streamVideoClient.user.id !== user.uid) {
      isConnectingRef.current = true;
      setLoadingMessage("Authenticating...");
      try {
        // Disconnect any existing user to avoid consecutive connect warning
        if (streamVideoClient.user) {
          await streamVideoClient.disconnectUser();
        }

        const token = await fetchVideoToken(user.uid);
        await streamVideoClient.connectUser(
          {
            id: user.uid,
            name: user.name || "User",
            image: user.photoUrl || "",
            userType: user.userType || "student",
          },
          token,
        );
      } catch (err) {
        console.error(
          "[useVideoCall] Failed to connect user to Stream Video:",
          err,
        );
        throw err;
      } finally {
        isConnectingRef.current = false;
      }
    }
  }, [user]);

  /**
   * Gets exam prep channel ID if applicable
   */
  const getExamPrepChannelId = useCallback(async () => {
    try {
      const { getUserChannels } = await import("../../api/examPrepApi");
      const channels = await getUserChannels(user.uid);
      const classChannel = channels.find(
        (channel) =>
          channel.classId === classId ||
          channel.videoChatId === classId ||
          channel.id === classId,
      );

      if (classChannel?.channelId) {
        console.log(
          "[useVideoCall] Found exam prep channel ID:",
          classChannel.channelId,
        );
        return classChannel.channelId;
      }
    } catch (apiError) {
      console.error(
        `[useVideoCall] Error fetching exam prep channel:`,
        apiError,
      );
    }
    return null;
  }, [classId, user]);

  /**
   * Joins a video call room
   */
  const joinRoom = useCallback(
    async (roomId) => {
      try {
        setIsLoading(true);
        setLoadingMessage("Connecting to call...");

        if (!user?.uid) {
          console.error("[useVideoCall] No valid user found");
          alert(
            "You need to be logged in to join a call. Please log in and try again.",
          );
          setIsLoading(false);
          return;
        }

        // Fetch class admin ID
        let classAdminId = null;
        try {
          const classDocRef = doc(db, "classes", roomId);
          const classDocSnap = await getDoc(classDocRef);
          if (classDocSnap.exists()) {
            classAdminId = classDocSnap.data().adminId;
          }
        } catch (err) {
          console.warn("[useVideoCall] Could not fetch class adminId", err);
        }

        // Ensure Stream Chat connection
        await ensureStreamChatConnection();

        // Ensure Stream Video connection
        await ensureStreamVideoConnection();

        // Leave previous call if exists
        if (callInstanceRef.current) {
          setLoadingMessage("Switching rooms...");
          await callInstanceRef.current.leave();
          callInstanceRef.current = null;
          setCurrentCall(null);
        }

        // Prepare call
        const callType = "default";
        const callId = roomId;
        setLoadingMessage("Joining video call...");
        const call = streamVideoClient.call(callType, callId);

        let channelId = null;
        if (classData?.classType === "exam_prep") {
          const examPrepChannelId = await getExamPrepChannelId();
          if (examPrepChannelId) {
            channelId = examPrepChannelId;
          }
        }
        if (!channelId) {
          channelId = generateChannelId(classId, roomId !== classId ? roomId : null);
        }

        // Add custom data to link video call with chat channel
        const callData = {
          custom: {
            channelCid: channelId,
            classId: classId,
          },
        };

        // Join the call
        try {
          await Promise.race([
            call.join({ create: true, data: callData }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Join call timeout")), 30000),
            ),
          ]);
        } catch (joinErr) {
          // If join fails, try to explicitly create the call
          console.warn(
            "[useVideoCall] call.join failed, attempting to create call and retry join",
            joinErr,
          );
          try {
            if (classAdminId) {
              await call.create({
                ...callData,
                created_by_id: classAdminId,
              });
            } else {
              await call.create(callData);
            }
            await call.join({ create: false, data: callData });
          } catch (createErr) {
            console.error(
              "[useVideoCall] Failed to create and join call:",
              createErr,
            );
            throw createErr;
          }
        }

        // Update state after successful join
        callInstanceRef.current = call;
        setCurrentCall(call);
        setIsCallJoined(true); 

        // Enable devices
        setLoadingMessage("Enabling camera and microphone...");
        await enableDevices(call);

        setIsLoading(false);

        // Callback if provided
        if (onCallJoined) {
          onCallJoined(call);
        }
      } catch (error) {
        console.error("[useVideoCall] Error joining call:", error);
        setIsLoading(false);
        if (error.message === "Join call timeout") {
          alert(
            "Connection timed out. Please check your network and try again.",
          );
        } else {
          alert(
            "Could not join the video call. Please check your connection and try again.",
          );
        }
      }
    },
    [
      user,
      classId,
      classData,
      ensureStreamChatConnection,
      ensureStreamVideoConnection,
      getExamPrepChannelId,
      enableDevices,
      onCallJoined,
    ],
  );

  /**
   * Triggers navigation callback (only once)
   */
  const triggerNavigation = useCallback(() => {
    if (!hasTriggeredNavigationRef.current && onCallLeft) {
      hasTriggeredNavigationRef.current = true;
      // Use setTimeout to ensure navigation happens after state updates
      setTimeout(() => {
        try {
          onCallLeft();
        } catch (error) {
          console.error("[useVideoCall] Error calling onCallLeft:", error);
        }
      }, 100);
    }
  }, [onCallLeft]);

  /**
   * Leaves the current call
   */
  const leaveCall = useCallback(async () => {
    if (isLeaving || hasTriggeredNavigationRef.current) {
      return; // Already leaving or navigation triggered
    }

    if (callInstanceRef.current) {
      try {
        setIsLeaving(true);
        
        // Check if call is already left to avoid duplicate leave attempts
        const callState = callInstanceRef.current.state;
        if (callState?.left || callState?.ended) {
          // Call already left, just clean up and call callback
          callInstanceRef.current = null;
          setCurrentCall(null);
          setIsCallJoined(false);
          triggerNavigation();
          return;
        }

        await callInstanceRef.current.leave();
        callInstanceRef.current = null;
        setCurrentCall(null);
        setIsCallJoined(false);
        triggerNavigation();
      } catch (error) {
        // If error is "already left", just clean up and call callback
        if (error.message?.includes("already been left") || error.message?.includes("already left")) {
          callInstanceRef.current = null;
          setCurrentCall(null);
          setIsCallJoined(false);
          triggerNavigation();
        } else {
          console.error("[useVideoCall] Error leaving call:", error);
          setIsLeaving(false); // Reset on error
        }
      }
    }
  }, [isLeaving, triggerNavigation]);

  // Set up reconnection handlers and call end/left listener
  useEffect(() => {
    if (currentCall) {
      const handleReconnect = () => {
        setLoadingMessage("Reconnecting to call...");
        setIsLoading(true);
      };

      const handleReconnected = () => {
        setIsLoading(false);
      };

      const handleCallEnded = () => {
        // Call ended naturally (not by user leaving)
        if (hasTriggeredNavigationRef.current) return;
        
        callInstanceRef.current = null;
        setCurrentCall(null);
        setIsCallJoined(false);
        triggerNavigation();
      };

      const handleCallLeft = () => {
        if (hasTriggeredNavigationRef.current) {
          return;
        }
        
        callInstanceRef.current = null;
        setCurrentCall(null);
        setIsCallJoined(false);
        setIsLeaving(false);
        triggerNavigation();
      };

      const checkCallState = () => {
        if (hasTriggeredNavigationRef.current) return;
        
        const callState = currentCall.state;
        const isLeft = callState?.left;
        const isEnded = callState?.ended;
        
        if (isLeft === true || isEnded === true) {
          callInstanceRef.current = null;
          setCurrentCall(null);
          setIsCallJoined(false);
          setIsLeaving(false);
          triggerNavigation();
        }
      };

      const stateCheckInterval = setInterval(checkCallState, 200);
      
      if (currentCall.state && typeof currentCall.state === 'object') {
        // Some Stream SDK versions support state observers
        try {
          currentCall.on("call.state_changed", checkCallState);
        } catch (e) {
          // State observer might not be available
        }
      }

      currentCall.on("reconnecting", handleReconnect);
      currentCall.on("connected", handleReconnected);
      currentCall.on("call.ended", handleCallEnded);
      currentCall.on("call.left", handleCallLeft);

      return () => {
        clearInterval(stateCheckInterval);
        currentCall.off("reconnecting", handleReconnect);
        currentCall.off("connected", handleReconnected);
        currentCall.off("call.ended", handleCallEnded);
        currentCall.off("call.left", handleCallLeft);
        try {
          currentCall.off("call.state_changed", checkCallState);
        } catch (e) {
          // Ignore if not available
        }
      };
    } else {
      // Reset flags when call is cleared
      hasTriggeredNavigationRef.current = false;
      setIsLeaving(false);
    }
  }, [currentCall, triggerNavigation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (callInstanceRef.current) {
        callInstanceRef.current.leave().catch(console.error);
      }
    };
  }, []);

  return {
    currentCall,
    isCallJoined,
    isLoading,
    loadingMessage,
    isLeaving,
    joinRoom,
    leaveCall,
    setLoadingMessage,
  };
};

